import { GlossaryHelperTypes } from './types'

/**
 * 术语表处理器工具函数
 */

/**
 * 生成占位符标记
 * @param termId 术语ID
 * @param prefix 占位符前缀，默认为 't'
 * @returns 占位符字符串，如 '<t1></t1>'
 */
export function generatePlaceholder(termId: number, prefix: string = 't'): string {
  return `<${prefix}${termId}></${prefix}${termId}>`
}

/**
 * 解析占位符标记，提取索引
 * @param placeholder 占位符字符串
 * @param prefix 占位符前缀，默认为 't'
 * @returns 解析出的索引，如果解析失败返回 null
 */
export function parsePlaceholder(placeholder: string, prefix: string = 't'): number | null {
  const regex = new RegExp(`<${prefix}(\\d+)></${prefix}>`)
  const match = placeholder.match(regex)
  return match ? parseInt(match[1], 10) : null
}

/**
 * 验证术语数据的有效性
 * @param glossaryData 术语表数据
 * @returns 验证结果和错误信息
 */
export function validateGlossaryData(glossaryData: GlossaryHelperTypes.GlossaryData): {
  valid: boolean
  errors: string[]
} {
  const errors: string[] = []

  if (!glossaryData.fromLang || typeof glossaryData.fromLang !== 'string') {
    errors.push('无效的源语言代码')
  }

  if (!glossaryData.toLang || typeof glossaryData.toLang !== 'string') {
    errors.push('无效的目标语言代码')
  }

  if (!glossaryData.fromText || typeof glossaryData.fromText !== 'string') {
    errors.push('无效的源文本')
  }

  if (!glossaryData.toText || typeof glossaryData.toText !== 'string') {
    errors.push('无效的目标文本')
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * 创建正则表达式用于术语匹配
 * @param term 术语
 * @param options 匹配选项
 * @returns 正则表达式
 */
export function createTermRegex(
  term: string,
  options: GlossaryHelperTypes.MatchConfig = {}
): RegExp {
  const { caseSensitive = true, wholeWord = true } = options

  // 转义正则表达式特殊字符
  const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // 构建正则表达式模式
  let pattern = escapedTerm

  if (wholeWord) {
    pattern = `\\b${pattern}\\b`
  }

  const flags = caseSensitive ? 'g' : 'gi'
  return new RegExp(pattern, flags)
}

/**
 * 检测术语冲突（重叠或嵌套的术语）
 * @param terms 术语列表
 * @param text 待检测文本
 * @returns 冲突检测结果
 */
export function detectTermConflicts(
  terms: GlossaryHelperTypes.GlossaryData[],
  text: string
): {
  hasConflicts: boolean
  conflicts: Array<{
    term1: string
    term2: string
    position: number
    message: string
  }>
} {
  const conflicts: Array<{
    term1: string
    term2: string
    position: number
    message: string
  }> = []

  for (let i = 0; i < terms.length; i++) {
    for (let j = i + 1; j < terms.length; j++) {
      const term1 = terms[i]
      const term2 = terms[j]

      // 检查是否有术语包含另一个术语
      if (term1.fromText.includes(term2.fromText) || term2.fromText.includes(term1.fromText)) {
        const position = text.indexOf(
          term1.fromText.includes(term2.fromText) ? term1.fromText : term2.fromText
        )
        if (position !== -1) {
          conflicts.push({
            term1: term1.fromText,
            term2: term2.fromText,
            position,
            message: `术语 "${term1.fromText}" 和 "${term2.fromText}" 存在包含关系`,
          })
        }
      }
    }
  }

  return {
    hasConflicts: conflicts.length > 0,
    conflicts,
  }
}

/**
 * 清理和标准化术语文本
 * @param text 原始文本
 * @returns 清理后的文本
 */
export function normalizeTermText(text: string): string {
  return text.trim().replace(/\s+/g, ' ')
}

/**
 * 获取文本中所有占位符的位置信息
 * @param text 包含占位符的文本
 * @param prefix 占位符前缀
 * @returns 占位符位置信息数组
 */
export function findAllPlaceholders(
  text: string,
  prefix = 't'
): Array<{ placeholder: string; start: number; end: number; termId: number }> {
  const regexPattern = `<${prefix}(\\d+)></${prefix}\\1>`
  const regex = new RegExp(regexPattern, 'g')

  const placeholders: Array<{ placeholder: string; start: number; end: number; termId: number }> =
    []

  let match
  while ((match = regex.exec(text)) !== null) {
    const termId = parseInt(match[1], 10)
    const placeholder = match[0]
    const start = match.index
    const end = start + placeholder.length

    placeholders.push({
      placeholder,
      start,
      end,
      termId,
    })
  }
  return placeholders
}

/**
 * 批量替换文本中的占位符
 * @param text 原始文本
 * @param replacements 替换映射
 * @returns 替换后的文本
 */
export function batchReplacePlaceholders(
  text: string,
  replacements: Array<{ placeholder: string; targetTerm: string }>
): string {
  let result = text

  // 按占位符长度降序排列，避免替换时的位置偏移问题
  const sortedReplacements = [...replacements].sort(
    (a, b) => b.placeholder.length - a.placeholder.length
  )

  for (const { placeholder, targetTerm } of sortedReplacements) {
    result = result.replace(
      new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
      targetTerm
    )
  }

  return result
}
